import { PaginatedAsyncComboBox } from '../../cdl/ComboBox/ComboBox';
import type { PaginatedAsyncComboBoxProps } from '../../cdl/ComboBox/ComboBox';
import { getOrder, getOrders } from '../../common/api/clients/gateway.api';
import { queries } from '../../common/api/queryKeys/queries';
import type { OrderModel } from '../../order/model/OrderModel';

import { IntegrationOrderDoubleLineOption } from './IntegrationOrderDoubleLineOption';

export interface IntegrationOrderSelectOption {
    label: string;
    value: string;
    order: OrderModel;
}

interface IntegrationOrderSelectProps
    extends Omit<
        PaginatedAsyncComboBoxProps<IntegrationOrderSelectOption>,
        'onChange' | 'value' | 'optionsQueryKey' | 'optionsQueryFn' | 'selectedOptionQueryKey' | 'selectedOptionQueryFn'
    > {
    value: string | undefined;
    onChange: (option: IntegrationOrderSelectOption) => void;
    states?: string[] | string;
}

export function IntegrationOrderSelect({ value, onChange, states, ...props }: IntegrationOrderSelectProps) {
    const toOption = (order: OrderModel): IntegrationOrderSelectOption => ({
        label: order.buyerReference || order.orderNumber || order.id,
        value: order.id,
        order,
    });

    const loadOrdersBySearchQuery = async (searchQuery: string, page: number) => {
        const ordersPage = await getOrders({ searchQuery, states, page });

        return {
            ...ordersPage,
            content: ordersPage.content.map(toOption),
        };
    };

    const loadOrderById = async (orderId: string): Promise<IntegrationOrderSelectOption> => {
        const order = await getOrder(orderId);
        return toOption(order);
    };

    return (
        <PaginatedAsyncComboBox<IntegrationOrderSelectOption>
            {...props}
            value={value || null}
            onChange={(_, meta) => {
                if (meta.option) {
                    onChange(meta.option);
                }
            }}
            optionsQueryKey={(search) => queries.orders.comboBoxOptions({ searchQuery: search, states }).queryKey}
            optionsQueryFn={({ search, page }) => loadOrdersBySearchQuery(search, page)}
            selectedOptionQueryKey={(id) => queries.orders.selectedComboBoxOption(id).queryKey}
            selectedOptionQueryFn={loadOrderById}
            OptionComponent={IntegrationOrderDoubleLineOption}
        />
    );
}
